import type {
  MapBiomeRegionAttemptEvent,
  MapTileAttemptEvent,
  MapObjectAttemptEvent,
  NpcAttemptEvent,
  PathTileAttemptEvent,
  PortalAttemptEvent,
} from '../../controller/InputController';
import type { EventHub } from '../../events/EventHub';
import type { GameState } from '../../model/gamestate/GameState';
import type { AuthoredTileMapCell, AuthoredTileMapData, BiomeType, MapDefinition, TileData } from '../../model/gamestate/GameState';
import type { SpaceTimeClient } from '../../spacetime/SpaceTimeClient';
import type { PlayerLookupHandler } from '../../ui/admin';
import { ActionLogger } from '../../utils/ActionLogger';
import { t } from '../../i18n';
import type { TileMapRenderer } from '../../renderer/tilemap/TileMapRenderer';
import {
  recommendPathTileAt,
} from '../../terrain/pathAutotiler/recommendPathTile';
import { applySinglePathBorderContinuationEdit, applySinglePathTileEdit } from '../../terrain/pathAutotiler/applySinglePathTileEdit';
import { encodeFixedPathVariantId } from '../../terrain/pathAutotiler/pathManualVariant';
import { getPathTileLayerDefinition } from '../../terrain/pathTileRegistry';
import { normalizeMapObjectPlacement } from '../../maps/mapObjectPlacement';
import { getMapObjectVisualDefinition } from '../../content/objectVisualRegistry';
import { resolveTileDrawMetrics } from '../../renderer/tilemap/board';
import type { AdminMapEditController } from './mapEditController';

interface CreateAdminPlayerHandlersParams {
  client: SpaceTimeClient;
  gameState: GameState;
}

interface RegisterAdminNpcHandlersParams {
  eventHub: EventHub;
  client: SpaceTimeClient;
  gameState: GameState;
  config: { playerId: string };
  refreshNpcs: (mapId: string, force?: boolean) => Promise<void>;
}

interface RegisterAdminMapHandlersParams {
  eventHub: EventHub;
  client: SpaceTimeClient;
  gameState: GameState;
  renderer: TileMapRenderer;
  controller: AdminMapEditController;
}

interface RegisterAdminMapObjectHandlersParams {
  eventHub: EventHub;
  client: SpaceTimeClient;
  gameState: GameState;
  renderer: TileMapRenderer;
  controller: AdminMapEditController;
}

const VISUAL_ASSET_REMOVE_RADIUS_PX = 100;

export interface AdminPlayerHandlers {
  scanPlayersOnMap: PlayerLookupHandler;
  disconnectPlayerSession: PlayerLookupHandler;
  bringPlayerHere: PlayerLookupHandler;
}

export function createAdminPlayerHandlers(params: CreateAdminPlayerHandlersParams): AdminPlayerHandlers {
  const { client, gameState } = params;

  const scanPlayersOnMap: PlayerLookupHandler = async (rawName) => {
    const query = rawName.trim();
    if (!query) {
      return { exists: false, message: t('admin.errors.enterSearch') };
    }

    const mapId = gameState.getCurrentMapId();
    try {
      const roster = await client.players.fetchPlayersOnMap(mapId);
      const lower = query.toLowerCase();
      const match = roster.find((candidate) => {
        const display = candidate.displayName ?? '';
        return display.toLowerCase() === lower || candidate.playerId.toLowerCase() === lower;
      });

      const exists = Boolean(match);
      const playerLabel = match?.displayName ?? match?.playerId ?? query;
      const message = exists
        ? t('admin.messages.playerOnMap', { name: playerLabel, mapId })
        : t('admin.messages.playerNotOnMap', { name: query, mapId });

      console.log('[Admin] Player search:', message);
      return { exists, message };
    } catch (error) {
      console.error('Failed to scan players on map', error);
      return { exists: false, message: t('admin.messages.scanUnavailable') };
    }
  };

  const disconnectPlayerSession: PlayerLookupHandler = async (rawName) => {
    const query = rawName.trim();
    if (!query) {
      return { exists: false, message: t('admin.errors.enterDisconnect') };
    }

    try {
      await client.admin.disconnectPlayerSession(query);
      const message = t('admin.messages.sessionTerminated', { name: query });
      console.log('[Admin] Player disconnect:', message);
      return { exists: true, message };
    } catch (error) {
      const message = error instanceof Error ? error.message : t('admin.messages.disconnectError');
      console.error('Failed to disconnect player session', error);
      return { exists: false, message };
    }
  };

  const bringPlayerHere: PlayerLookupHandler = async (rawName) => {
    const query = rawName.trim();
    if (!query) {
      return { exists: false, message: t('admin.errors.enterTeleport') };
    }

    try {
      await client.admin.bringPlayerHere(query);
      const message = t('admin.messages.playerMoved', { name: query });
      console.log('[Admin] Bring player:', message);
      return { exists: true, message };
    } catch (error) {
      const message = error instanceof Error ? error.message : t('admin.messages.moveFailed');
      console.error('Failed to move player', error);
      return { exists: false, message };
    }
  };

  return {
    scanPlayersOnMap,
    disconnectPlayerSession,
    bringPlayerHere,
  };
}

export function registerAdminNpcHandlers(params: RegisterAdminNpcHandlersParams): () => void {
  const { eventHub, client, gameState, config, refreshNpcs } = params;

  const handleNpcAttempt = (event: NpcAttemptEvent): void => {
    const { action, npcType, x, y } = event;
    const mapId = gameState.getCurrentMapId();

    if (action === 'place') {
      ActionLogger.attempt('Place NPC', { details: { type: npcType, x, y } });
      if (!npcType) return;
      void client.admin.placeNpc(
        mapId ?? 'default',
        x,
        y,
        npcType,
        JSON.stringify({ created_by: config.playerId })
      )
        .then(() => {
          ActionLogger.success('Place NPC');
          void refreshNpcs(mapId, true);
        })
        .catch((error) => ActionLogger.failure('Place NPC', error));
      return;
    }

    if (action === 'remove') {
      ActionLogger.attempt('Remove NPC', { details: { x, y } });
      void client.admin.removeNpc(mapId, x, y)
        .then(() => {
          ActionLogger.success('Remove NPC');
          void refreshNpcs(mapId, true);
        })
        .catch((error) => ActionLogger.failure('Remove NPC', error));
    }
  };

  eventHub.on('admin:npcAttempt', handleNpcAttempt);
  return () => {
    eventHub.off('admin:npcAttempt', handleNpcAttempt);
  };
}

export function registerAdminMapHandlers(params: RegisterAdminMapHandlersParams): () => void {
  const { eventHub, client, gameState, renderer, controller } = params;

  const handleMapTileAttempt = (event: MapTileAttemptEvent): void => {
    const { layer, value, x, y } = event;
    const mapId = gameState.getCurrentMapId();
    if (layer === 'biome' && applyFrontendAuthoredTerrainEdit(gameState, renderer, x, y, value)) {
      ActionLogger.success('Edit map terrain');
      return;
    }
    const terrainValue =
      layer === 'terrain'
        ? value === '__erase__' || value === 'walkable'
          ? 'floor'
          : value === 'path'
            ? 'path'
            : 'wall'
        : '';
    const biomeValue =
      layer === 'biome'
        ? value === '__erase__'
          ? 'default'
          : value
        : '';
    ActionLogger.attempt('Edit map tile', { details: { mapId, layer, value, x, y } });
    void controller.runMutation(async () => {
      await client.admin.setMapTile(
        mapId,
        x,
        y,
        terrainValue,
        biomeValue
      );
        await controller.refreshMap(mapId, { refreshRenderer: true });
        ActionLogger.success('Edit map tile');
      })
      .catch((error) => ActionLogger.failure('Edit map tile', error));
  };

  const handlePortalAttempt = (event: PortalAttemptEvent): void => {
    const { action, x, y, destMapId, destX, destY, autoPortal } = event;
    const mapId = gameState.getCurrentMapId();
    if (action === 'remove') {
      ActionLogger.attempt('Remove portal', { details: { mapId, x, y } });
      void controller.runMutation(async () => {
        await client.admin.removePortal(mapId, x, y);
          await controller.refreshMap(mapId, { refreshRenderer: true });
          ActionLogger.success('Remove portal');
        })
        .catch((error) => ActionLogger.failure('Remove portal', error));
      return;
    }

    if (!destMapId) {
      ActionLogger.failure('Add portal', new Error('Portal destination map is required.'));
      return;
    }

    if (autoPortal) {
      ActionLogger.attempt('Add auto portal', { details: { mapId, x, y, destMapId } });
      void controller.runMutation(async () => {
        await client.admin.addPortalAuto(mapId, x, y, destMapId);
        await controller.refreshMap(mapId, { refreshRenderer: true });
        if (destMapId !== mapId) {
          await controller.refreshMap(destMapId, { refreshRenderer: false });
        }
        ActionLogger.success('Add auto portal');
      })
      .catch((error) => ActionLogger.failure('Add auto portal', error));
      return;
    }

    if (!Number.isFinite(destX) || !Number.isFinite(destY)) {
      ActionLogger.failure('Add portal', new Error('Portal destination map and coordinates are required.'));
      return;
    }

    ActionLogger.attempt('Add portal', { details: { mapId, x, y, destMapId, destX, destY } });
    void controller.runMutation(async () => {
      await client.admin.addPortal(mapId, x, y, destMapId, destX, destY);
        await controller.refreshMap(mapId, { refreshRenderer: true });
        ActionLogger.success('Add portal');
      })
      .catch((error) => ActionLogger.failure('Add portal', error));
  };

  const handleMapBiomeRegionAttempt = (event: MapBiomeRegionAttemptEvent): void => {
    const mapId = gameState.getCurrentMapId();
    const minX = Math.min(event.startX, event.endX);
    const maxX = Math.max(event.startX, event.endX);
    const minY = Math.min(event.startY, event.endY);
    const maxY = Math.max(event.startY, event.endY);
    ActionLogger.attempt('Edit biome region', {
      details: {
        mapId,
        biome: event.biome,
        startX: minX,
        startY: minY,
        endX: maxX,
        endY: maxY,
      },
    });
    void controller.runMutation(async () => {
      await client.admin.setMapBiomeRegion(
        mapId,
        minX,
        minY,
        maxX - minX + 1,
        maxY - minY + 1,
        event.biome,
      );
      await controller.refreshMap(mapId, { refreshRenderer: true });
      ActionLogger.success('Edit biome region');
    }).catch((error) => ActionLogger.failure('Edit biome region', error));
  };

  const handlePathTileAttempt = (event: PathTileAttemptEvent): void => {
    const mapId = gameState.getCurrentMapId();
    const currentMap = gameState.getMap();
    const inBounds = event.x >= 0 && event.x < currentMap.width && event.y >= 0 && event.y < currentMap.height;
    const autotile = event.autotile === true;
    const placeTile = autotile || typeof event.tileIndex === 'number';
    const recommendation = autotile && inBounds
      ? recommendPathTileAt(currentMap, event.atlasId, event.x, event.y)
      : null;
    const selectedManualTileIndex = typeof event.tileIndex === 'number' ? event.tileIndex : -1;
    const resolvedTileIndex = placeTile
      ? (autotile ? recommendation?.tileIndex : selectedManualTileIndex) ?? -1
      : -1;
    const resolvedFlipHorizontal = placeTile
      ? autotile
        ? recommendation?.flipHorizontal === true
        : event.flipHorizontal === true
      : false;
    const resolvedFlipVertical = placeTile
      ? autotile
        ? recommendation?.flipVertical === true
        : event.flipVertical === true
      : false;
    const resolvedRotationSteps = placeTile
      ? autotile
        ? recommendation?.rotationSteps ?? 0
        : typeof event.rotationSteps === 'number' ? event.rotationSteps : 0
      : 0;
    const pathTileDetails = {
      mapId,
      atlasId: event.atlasId,
      x: event.x,
      y: event.y,
      action: autotile ? 'autotile-place' : typeof event.tileIndex === 'number' ? 'place' : 'erase',
      tileIndex: resolvedTileIndex,
      flipHorizontal: resolvedFlipHorizontal,
      flipVertical: resolvedFlipVertical,
      rotationSteps: resolvedRotationSteps,
    };
    if (!getPathTileLayerDefinition(event.atlasId) || (autotile && inBounds && !recommendation)) {
      ActionLogger.failure(
        'Edit map path tile',
        new Error(`Path tile editing is not available for atlas '${event.atlasId}' on ${currentMap.gridType} maps.`),
        { details: pathTileDetails },
      );
      return;
    }
    if (!inBounds && !autotile && typeof event.tileIndex === 'number') {
      ActionLogger.failure(
        'Edit map path tile',
        new Error('Manual path tiles can only be placed on the board. Use autotile mode for off-board ghost path continuations.'),
        { details: pathTileDetails },
      );
      return;
    }
    ActionLogger.attempt('Edit map path tile', { details: pathTileDetails });
    void controller.runMutation(async () => {
      if (!inBounds) {
        const ghostActive = autotile;
        await client.admin.setMapPathBorderContinuation(
          mapId,
          event.atlasId,
          event.x,
          event.y,
          ghostActive,
        );
        applySinglePathBorderContinuationEdit(gameState, renderer, {
          atlasId: event.atlasId,
          x: event.x,
          y: event.y,
          active: ghostActive,
        });
        ActionLogger.success('Edit map path tile');
        return;
      }

      const centerCell = placeTile
        ? autotile
          ? {
              mode: 'autotile' as const,
            }
          : (() => {
              const variantId = encodeFixedPathVariantId(currentMap.gridType, resolvedTileIndex);
              if (!variantId) {
                throw new Error(`Tile index ${resolvedTileIndex} is not a valid fixed path brush for ${currentMap.gridType} maps.`);
              }
              return {
                mode: 'fixed' as const,
                variantId,
                flipHorizontal: resolvedFlipHorizontal,
                flipVertical: resolvedFlipVertical,
                rotationSteps: resolvedRotationSteps,
              };
            })()
        : null;

      await client.admin.setMapPathCell(
        mapId,
        event.atlasId,
        event.x,
        event.y,
        centerCell,
      );
      applySinglePathTileEdit(gameState, renderer, {
        atlasId: event.atlasId,
        x: event.x,
        y: event.y,
        cell: centerCell,
      });
      client.maps.clearMapDefinitionCache(mapId);
      ActionLogger.success('Edit map path tile');
    })
      .catch((error) => {
        console.error('[Admin path tile edit rejected]', {
          ...pathTileDetails,
          serverError: error instanceof Error ? error.message : String(error),
        });
        ActionLogger.failure('Edit map path tile', error, { details: pathTileDetails });
      });
  };

  eventHub.on('admin:mapTileAttempt', handleMapTileAttempt);
  eventHub.on('admin:mapBiomeRegionAttempt', handleMapBiomeRegionAttempt);
  eventHub.on('admin:pathTileAttempt', handlePathTileAttempt);
  eventHub.on('admin:portalAttempt', handlePortalAttempt);
  return () => {
    eventHub.off('admin:mapTileAttempt', handleMapTileAttempt);
    eventHub.off('admin:mapBiomeRegionAttempt', handleMapBiomeRegionAttempt);
    eventHub.off('admin:pathTileAttempt', handlePathTileAttempt);
    eventHub.off('admin:portalAttempt', handlePortalAttempt);
  };
}

const AUTHORED_GRASS_GID = 4;
const AUTHORED_PATH_GID = 278;
const AUTHORED_WATER_GID = 3758096595;

function applyFrontendAuthoredTerrainEdit(
  gameState: GameState,
  renderer: TileMapRenderer,
  x: number,
  y: number,
  value: string,
): boolean {
  const map = gameState.getMap();
  const authoredTileMap = map.authoredTileMap;
  if (map.gridType !== 'square' || !authoredTileMap) {
    return false;
  }

  const grassLayerIndex = authoredTileMap.layers.findIndex((layer) => layer.name === 'grass');
  const riverPathLayerIndex = authoredTileMap.layers.findIndex((layer) => layer.name === 'river+path');
  if (grassLayerIndex < 0 || riverPathLayerIndex < 0) {
    return false;
  }

  const cellIndex = (y * map.width) + x;
  const placement = resolveAuthoredTerrainPlacement(value);
  const nextLayers = authoredTileMap.layers.map((layer, index) => {
    if (index !== grassLayerIndex && index !== riverPathLayerIndex) {
      return layer;
    }
    const nextTiles = [...layer.tiles];
    nextTiles[cellIndex] = index === grassLayerIndex ? placement.grass : placement.riverPath;
    return {
      ...layer,
      tiles: nextTiles,
    };
  });

  const nextTiles = map.tiles.map((row) => row.map((tile) => ({ ...tile })));
  const currentTile = nextTiles[y]?.[x];
  if (!currentTile) {
    return false;
  }
  nextTiles[y][x] = {
    ...currentTile,
    biome: placement.biome,
  };

  const nextMap: MapDefinition = {
    ...map,
    tiles: nextTiles,
    authoredTileMap: {
      ...authoredTileMap,
      layers: nextLayers,
    },
  };
  gameState.setMap(nextMap, gameState.getCurrentMapId());
  renderer.refreshCurrentMap(nextMap);
  return true;
}

function resolveAuthoredTerrainPlacement(value: string): {
  grass: AuthoredTileMapCell;
  riverPath: AuthoredTileMapCell;
  biome: BiomeType;
} {
  if (value === '__erase__' || value === 'default') {
    return {
      grass: emptyAuthoredTile(),
      riverPath: emptyAuthoredTile(),
      biome: 'default',
    };
  }
  if (value === 'grass' || value === 'soft_grass' || value === 'realistic_grass' || value === 'realistic_grass2') {
    return {
      grass: authoredTileFromRawGid(AUTHORED_GRASS_GID),
      riverPath: emptyAuthoredTile(),
      biome: 'grass',
    };
  }
  if (value === 'water' || value === 'realistic_water') {
    return {
      grass: emptyAuthoredTile(),
      riverPath: authoredTileFromRawGid(AUTHORED_WATER_GID),
      biome: 'water',
    };
  }
  return {
    grass: emptyAuthoredTile(),
    riverPath: authoredTileFromRawGid(AUTHORED_PATH_GID),
    biome: normalizeBiomeValue(value),
  };
}

function normalizeBiomeValue(value: string): BiomeType {
  const biome = value.trim().toLowerCase();
  return biome.length > 0 ? biome : 'default';
}

function emptyAuthoredTile(): AuthoredTileMapCell {
  return {
    gid: 0,
    flipHorizontal: false,
    flipVertical: false,
    flipDiagonal: false,
  };
}

function authoredTileFromRawGid(rawGid: number): AuthoredTileMapCell {
  return {
    gid: rawGid & 0x1fffffff,
    flipHorizontal: (rawGid & 0x80000000) !== 0,
    flipVertical: (rawGid & 0x40000000) !== 0,
    flipDiagonal: (rawGid & 0x20000000) !== 0,
  };
}

export function registerAdminMapObjectHandlers(params: RegisterAdminMapObjectHandlersParams): () => void {
  const { eventHub, client, gameState, renderer, controller } = params;

  const handleMapObjectAttempt = (event: MapObjectAttemptEvent): void => {
    const {
      action,
      assetId,
      mapX,
      mapY,
      placementMode,
      tileX,
      tileY,
      offsetX,
      offsetY,
      sizeMultiplier,
      flipHorizontal,
      flipVertical,
      rotationSteps,
      screenX,
      screenY,
    } = event;

    if (action === 'remove') {
      ActionLogger.attempt('Remove map object', { details: { mapX, mapY, screenX, screenY } });
      if (!Number.isFinite(screenX) || !Number.isFinite(screenY)) {
        return;
      }
      const objects = [...gameState.getMap().objects];
      let target: typeof objects[number] | null = null;
      let bestDistanceSquared = VISUAL_ASSET_REMOVE_RADIUS_PX * VISUAL_ASSET_REMOVE_RADIUS_PX;
      for (const object of objects) {
        const anchor = resolveMapObjectScreenAnchor(renderer, object);
        if (!anchor) {
          continue;
        }
        const dx = anchor.x - screenX;
        const dy = anchor.y - screenY;
        const distanceSquared = dx * dx + dy * dy;
        if (distanceSquared >= bestDistanceSquared) {
          continue;
        }
        target = object;
        bestDistanceSquared = distanceSquared;
      }
      if (!target) {
        return;
      }
      const mapId = gameState.getCurrentMapId();
      ActionLogger.attempt('Remove map object', { details: { mapId, objectId: target.id } });
      void controller.runMutation(async () => {
        await client.admin.removeMapObject(mapId, target.id);
          await controller.refreshMap(mapId, { refreshRenderer: true });
          ActionLogger.success('Remove map object');
        })
        .catch((error) => ActionLogger.failure('Remove map object', error));
      return;
    }

    if (!assetId) {
      return;
    }

    const placement = resolveMapObjectPlacement(assetId, {
      mapX,
      mapY,
      placementMode,
      tileX,
      tileY,
      offsetX,
      offsetY,
      sizeMultiplier,
      flipHorizontal,
      flipVertical,
      rotationSteps,
    });
    ActionLogger.attempt('Place map object', {
      details: {
        mapId: gameState.getCurrentMapId(),
        assetId,
        x: placement.x,
        y: placement.y,
        offsetX: placement.offsetX,
        offsetY: placement.offsetY,
        sizeMultiplier: placement.sizeMultiplier,
        flipHorizontal: placement.flipHorizontal,
        flipVertical: placement.flipVertical,
        rotationSteps: placement.rotationSteps,
      }
    });
    const mapId = gameState.getCurrentMapId();
    void controller.runMutation(async () => {
      await client.admin.addMapObject(
        mapId,
        '',
        assetId,
        placement.x,
        placement.y,
        placement.offsetX,
        placement.offsetY,
        placement.sizeMultiplier,
        placement.flipHorizontal,
        placement.flipVertical,
        placement.rotationSteps,
        'objects',
      );
        await controller.refreshMap(mapId, { refreshRenderer: true });
        ActionLogger.success('Place map object');
      })
      .catch((error) => ActionLogger.failure('Place map object', error));
  };

  eventHub.on('admin:mapObjectAttempt', handleMapObjectAttempt);
  return () => {
    eventHub.off('admin:mapObjectAttempt', handleMapObjectAttempt);
  };
}

function resolveMapObjectPlacement(
  _assetId: string,
  input: {
    mapX: number;
    mapY: number;
    placementMode?: 'cursor' | 'tile-anchor';
    tileX?: number;
    tileY?: number;
    offsetX?: number;
    offsetY?: number;
    sizeMultiplier?: number;
    flipHorizontal?: boolean;
    flipVertical?: boolean;
    rotationSteps?: number;
  },
): {
  x: number;
  y: number;
  offsetX: number;
  offsetY: number;
  sizeMultiplier: number;
  flipHorizontal: boolean;
  flipVertical: boolean;
  rotationSteps: number;
} {
  const placementTransform = {
    flipHorizontal: Boolean(input.flipHorizontal),
    flipVertical: Boolean(input.flipVertical),
    rotationSteps: Number.isFinite(input.rotationSteps) ? ((input.rotationSteps as number) % 12 + 12) % 12 : 0,
    sizeMultiplier: Number.isFinite(input.sizeMultiplier) && (input.sizeMultiplier as number) > 0
      ? Number(input.sizeMultiplier)
      : 1,
  };

  if (input.placementMode !== 'tile-anchor' || !Number.isFinite(input.tileX) || !Number.isFinite(input.tileY)) {
    return {
      x: Math.floor(input.mapX),
      y: Math.floor(input.mapY),
      offsetX: 0,
      offsetY: 0,
      ...placementTransform,
    };
  }

  return {
    x: input.tileX,
    y: input.tileY,
    offsetX: Number.isFinite(input.offsetX) ? input.offsetX! : 0,
    offsetY: Number.isFinite(input.offsetY) ? input.offsetY! : 0,
    ...placementTransform,
  };
}

function resolveMapObjectScreenAnchor(
  renderer: TileMapRenderer,
  object: { x: number; y: number; offsetX?: number; offsetY?: number },
): { x: number; y: number } | null {
  const visualId = 'asset' in object && typeof object.asset === 'string' && object.asset
    ? object.asset
    : 'id' in object && typeof object.id === 'string'
      ? object.id
      : null;
  if (!visualId || !getMapObjectVisualDefinition(visualId)) {
    return null;
  }
  const grid = renderer.getCurrentGrid();
  const normalized = normalizeMapObjectPlacement(object);
  const tileMetrics = resolveTileDrawMetrics(grid, 1, normalized.x, normalized.y);
  return renderer.mapPositionToScreen(
    tileMetrics.centerX + normalized.offsetX * tileMetrics.spanWidth,
    tileMetrics.centerY + normalized.offsetY * tileMetrics.spanHeight,
  ) ?? null;
}
